import React from 'react';
import {
  Fab,
  Zoom,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import {
  KeyboardArrowUp as ArrowUpIcon
} from '@mui/icons-material';
import { useScrollPosition } from '../../hooks/useScrollToTop';

interface ScrollToTopButtonProps {
  /** المسافة من الأسفل بالبكسل */
  bottom?: number;
  /** المسافة من اليمين بالبكسل */
  right?: number;
  /** الحد الأدنى للتمرير لإظهار الزر */
  showAfter?: number;
  /** حجم الزر */
  size?: 'small' | 'medium' | 'large';
  /** لون الزر */
  color?: 'primary' | 'secondary' | 'default';
  /** نص التلميح */
  tooltip?: string;
  /** دالة مخصصة للتمرير */
  onScrollToTop?: () => void;
}

const ScrollToTopButton: React.FC<ScrollToTopButtonProps> = ({
  bottom = 20,
  right = 20,
  showAfter = 300,
  size = 'medium',
  color = 'primary',
  tooltip = 'العودة للأعلى',
  onScrollToTop
}) => {
  const theme = useTheme();
  const { scrollY, isAtTop } = useScrollPosition();

  const handleScrollToTop = () => {
    if (onScrollToTop) {
      onScrollToTop();
    } else {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }
  };

  const shouldShow = scrollY > showAfter && !isAtTop;

  return (
    <Zoom in={shouldShow} timeout={300}>
      <Tooltip title={tooltip} placement="left">
        <Fab
          color={color}
          size={size}
          onClick={handleScrollToTop}
          sx={{
            position: 'fixed',
            bottom: bottom,
            right: right,
            zIndex: 1000,
            boxShadow: theme.shadows[8],
            backgroundColor: color === 'primary' 
              ? theme.palette.primary.main 
              : color === 'secondary' 
                ? theme.palette.secondary.main 
                : theme.palette.grey[700],
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: color === 'primary' 
                ? theme.palette.primary.dark 
                : color === 'secondary' 
                  ? theme.palette.secondary.dark 
                  : theme.palette.grey[800],
              transform: 'scale(1.1)',
              boxShadow: theme.shadows[12],
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            // تأثير النبضة
            animation: scrollY > showAfter * 2 ? 'pulse 2s infinite' : 'none',
            '@keyframes pulse': {
              '0%': {
                boxShadow: `0 0 0 0 ${alpha(
                  color === 'primary' 
                    ? theme.palette.primary.main 
                    : color === 'secondary' 
                      ? theme.palette.secondary.main 
                      : theme.palette.grey[700], 
                  0.7
                )}`,
              },
              '70%': {
                boxShadow: `0 0 0 10px ${alpha(
                  color === 'primary' 
                    ? theme.palette.primary.main 
                    : color === 'secondary' 
                      ? theme.palette.secondary.main 
                      : theme.palette.grey[700], 
                  0
                )}`,
              },
              '100%': {
                boxShadow: `0 0 0 0 ${alpha(
                  color === 'primary' 
                    ? theme.palette.primary.main 
                    : color === 'secondary' 
                      ? theme.palette.secondary.main 
                      : theme.palette.grey[700], 
                  0
                )}`,
              },
            },
          }}
        >
          <ArrowUpIcon />
        </Fab>
      </Tooltip>
    </Zoom>
  );
};

export default ScrollToTopButton;
